import {
  FeedbackError,
  answerErrors,
  isRecord,
  normalizeError,
  settlement
} from "./chunk-RNF7XJWR.js";

// src/core/context.ts
function buildFeedbackContext() {
  if (typeof window === "undefined") return {};
  const { origin, pathname } = window.location;
  return {
    route: pathname,
    url: `${origin}${pathname}`,
    user_agent: window.navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`
  };
}

// src/core/answers.ts
var RATING_DEFAULT_MIN = 1;
var RATING_DEFAULT_MAX = 10;
var TEXT_HARD_LIMIT = 1e4;
function textLength(text) {
  return Array.from(text).length;
}
function isAnswered(value) {
  if (value === null || value === void 0) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}
function ratingScale(question) {
  var _a, _b;
  return {
    min: (_a = question.min_value) != null ? _a : RATING_DEFAULT_MIN,
    max: (_b = question.max_value) != null ? _b : RATING_DEFAULT_MAX
  };
}
function textLimit(question) {
  return Math.min(question.max_length || TEXT_HARD_LIMIT, TEXT_HARD_LIMIT);
}
function cleanAnswers(form, values) {
  const questions = "questions" in form ? form.questions : form;
  const answers = {};
  const errors = {};
  for (const question of questions) {
    const value = Object.prototype.hasOwnProperty.call(values, question.key) ? values[question.key] : void 0;
    if (!isAnswered(value)) {
      if (question.required) errors[question.key] = "Is required.";
      continue;
    }
    answers[question.key] = typeof value === "string" ? value.trim() : value;
  }
  if (Object.keys(errors).length === 0 && Object.keys(answers).length === 0) {
    errors.answers = "Answer at least one question.";
  }
  return { answers, errors };
}
function formSignature(form) {
  return JSON.stringify(
    form.questions.map((q) => [q.key, q.type, q.required, q.choices, q.min_value, q.max_value, q.max_length])
  );
}

// src/core/promptController.ts
var RETRY_BASE_MS = 2e3;
var AUTH_RETRY_BASE_MS = 3e4;
var RETRY_MAX_MS = 15 * 6e4;
var broadcastChannelFactory = (name, onMessage) => {
  var _a;
  if (typeof BroadcastChannel !== "function") return null;
  try {
    const channel = new BroadcastChannel(name);
    channel.onmessage = (event) => onMessage(event.data);
    (_a = channel.unref) == null ? void 0 : _a.call(channel);
    return {
      post(message) {
        try {
          channel.postMessage(message);
        } catch (e) {
        }
      },
      close: () => channel.close()
    };
  } catch (e) {
    return null;
  }
};
var IDLE = { status: "idle" };
function readPrompt(body) {
  const isForm = (form) => form == null || isRecord(form) && Array.isArray(form.questions);
  return isRecord(body) && typeof body.due === "boolean" && isForm(body.periodic) && isForm(body.adhoc) ? body : null;
}
function sameValues(a, b) {
  return Object.keys(a).every((key) => a[key] === b[key]);
}
function withTimeout(request, timeoutMs, abort = new AbortController()) {
  let timer;
  const timeout = new Promise((_, reject) => {
    if (timeoutMs <= 0) return;
    timer = setTimeout(() => {
      abort.abort();
      reject(new FeedbackError({ status: 0, detail: "The feedback service took too long to answer." }));
    }, timeoutMs);
  });
  return Promise.race([Promise.resolve().then(() => request(abort.signal)), timeout]).finally(() => clearTimeout(timer));
}
function createPromptController(options) {
  var _a, _b;
  const {
    client,
    pollMs = 6e4,
    timeoutMs = 3e4,
    channelName = "huginn",
    createChannel = broadcastChannelFactory,
    stuckAfter = 2,
    context = buildFeedbackContext,
    onError
  } = options;
  let enabled = (_a = options.enabled) != null ? _a : true;
  let epoch = 0;
  let locationKey = (_b = options.locationKey) != null ? _b : "";
  let prompt = null;
  let status = "idle";
  let readError = null;
  let paused = false;
  let readFailures = 0;
  let retryAt = 0;
  let submission = IDLE;
  let failures = 0;
  let failuresCycle = null;
  let lastRefused = null;
  const doneCycles = /* @__PURE__ */ new Set();
  let sequence = 0;
  let inflight = null;
  let readAbort = null;
  const cancelRead = () => {
    readAbort == null ? void 0 : readAbort.abort();
    readAbort = null;
    sequence += 1;
    inflight = null;
  };
  const listeners = /* @__PURE__ */ new Set();
  let started = false;
  let disposed = false;
  let pollTimer = null;
  let channel = null;
  let focusTarget = null;
  let visibilityTarget = null;
  const report = (error, phase) => {
    try {
      onError == null ? void 0 : onError(error, phase);
    } catch (e) {
    }
  };
  let lastPeriodic = null;
  const derive = () => {
    var _a2, _b2, _c;
    const live = enabled && prompt !== null && prompt.enabled ? prompt : null;
    const cycleId = (_a2 = live == null ? void 0 : live.cycle_id) != null ? _a2 : null;
    const form = (live == null ? void 0 : live.due) && live.periodic && cycleId !== null && !doneCycles.has(cycleId) ? live.periodic : null;
    let periodic = null;
    if (form && cycleId !== null) {
      periodic = {
        form,
        // A due prompt without a mode is treated as required: never offer a skip the server refuses.
        mode: (_b2 = live == null ? void 0 : live.mode) != null ? _b2 : "required",
        cycleId,
        canContinue: failuresCycle === cycleId && failures >= stuckAfter
      };
      if (lastPeriodic && sameValues(lastPeriodic, periodic)) periodic = lastPeriodic;
    }
    lastPeriodic = periodic;
    return {
      status,
      enabled,
      due: periodic !== null,
      periodic,
      adhoc: (_c = live == null ? void 0 : live.adhoc) != null ? _c : null,
      submission,
      paused,
      error: readError
    };
  };
  let snapshot = derive();
  const shouldPoll = () => started && snapshot.due && pollMs > 0;
  const syncPoll = () => {
    if (shouldPoll() && pollTimer === null) {
      pollTimer = setInterval(() => {
        if ((visibilityTarget == null ? void 0 : visibilityTarget.visibilityState) === "hidden") return;
        autoLoad(false);
      }, pollMs);
    } else if (!shouldPoll() && pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };
  const update = () => {
    const next = derive();
    if (!sameValues(snapshot, next)) {
      snapshot = next;
      listeners.forEach((listener) => listener());
    }
    syncPoll();
  };
  function load(fresh) {
    if (!started || !enabled || disposed) return Promise.resolve();
    if (inflight && !fresh) return inflight;
    cancelRead();
    const abort = new AbortController();
    readAbort = abort;
    const mine = sequence;
    if (status === "idle") {
      status = "loading";
      update();
    }
    const run = (async () => {
      let next = null;
      let failure = null;
      try {
        next = readPrompt(await withTimeout((signal) => client.getPrompt({ signal }), timeoutMs, abort));
        if (next === null) failure = new FeedbackError({ status: 0, detail: "The prompt was not understood." });
      } catch (error) {
        failure = normalizeError(error);
      }
      if (mine !== sequence || disposed) return;
      inflight = null;
      readAbort = null;
      status = "ready";
      if (next !== null) {
        prompt = prompt !== null && JSON.stringify(next) === JSON.stringify(prompt) ? prompt : next;
        readError = null;
        paused = false;
        readFailures = 0;
        retryAt = 0;
      } else if (failure !== null) {
        readError = failure;
        if (failure.status === 401 || failure.status === 403) {
          paused = true;
          prompt = null;
        } else if (failure.status === 404) {
          prompt = null;
        }
        readFailures += 1;
        const base = paused ? AUTH_RETRY_BASE_MS : RETRY_BASE_MS;
        retryAt = Date.now() + Math.min(base * 2 ** (readFailures - 1), RETRY_MAX_MS);
        report(failure, "read");
      }
      update();
    })();
    inflight = run;
    return run;
  }
  function autoLoad(fresh) {
    if (started && Date.now() >= retryAt) void load(fresh);
  }
  const loadNow = () => {
    retryAt = 0;
    return load(true);
  };
  const onFocus = () => autoLoad(false);
  const onVisibility = () => {
    if ((visibilityTarget == null ? void 0 : visibilityTarget.visibilityState) !== "hidden") autoLoad(false);
  };
  const onChannelMessage = (data) => {
    if (!isRecord(data) || data.type !== "settled") return;
    if (typeof data.cycleId === "number") doneCycles.add(data.cycleId);
    if (submission.status === "error" && submission.kind !== "adhoc") submission = IDLE;
    update();
    autoLoad(true);
  };
  const start = () => {
    if (started || disposed) return;
    started = true;
    focusTarget = options.window !== void 0 ? options.window : typeof window === "undefined" ? null : window;
    visibilityTarget = options.document !== void 0 ? options.document : typeof document === "undefined" ? null : document;
    focusTarget == null ? void 0 : focusTarget.addEventListener("focus", onFocus);
    visibilityTarget == null ? void 0 : visibilityTarget.addEventListener("visibilitychange", onVisibility);
    channel = createChannel(channelName, onChannelMessage);
    if (enabled) void loadNow();
    else update();
  };
  const stop = () => {
    if (!started) return;
    started = false;
    focusTarget == null ? void 0 : focusTarget.removeEventListener("focus", onFocus);
    visibilityTarget == null ? void 0 : visibilityTarget.removeEventListener("visibilitychange", onVisibility);
    focusTarget = null;
    visibilityTarget = null;
    channel == null ? void 0 : channel.close();
    channel = null;
    cancelRead();
    syncPoll();
  };
  const broadcast = (message) => {
    if (channel) {
      channel.post(message);
      return;
    }
    const once = createChannel(channelName, () => {
    });
    once == null ? void 0 : once.post(message);
    once == null ? void 0 : once.close();
  };
  const settle = (cycleId, outcome, kind) => {
    doneCycles.add(cycleId);
    failures = 0;
    lastRefused = null;
    submission = kind === "skip" ? { status: "skipped", outcome } : { status: "thanked", kind: "periodic", outcome };
    broadcast({ type: "settled", cycleId });
    update();
    void load(true);
  };
  const readContext = () => {
    try {
      return context();
    } catch (e) {
      return {};
    }
  };
  async function send(kind, values) {
    var _a2, _b2, _c, _d, _e, _f;
    if (disposed || !enabled || submission.status === "sending") return "ignored";
    const current = snapshot;
    const form = kind === "adhoc" ? current.adhoc : (_b2 = (_a2 = current.periodic) == null ? void 0 : _a2.form) != null ? _b2 : null;
    const cycleId = (_d = (_c = current.periodic) == null ? void 0 : _c.cycleId) != null ? _d : null;
    if (!form || kind !== "adhoc" && cycleId === null) return "ignored";
    if (kind === "skip" && ((_e = current.periodic) == null ? void 0 : _e.mode) !== "optional") return "ignored";
    const answers = kind === "skip" ? {} : cleanAnswers(form, values).answers;
    const payload = { kind, answers, context: readContext() };
    if (kind !== "adhoc") payload.cycle_id = cycleId;
    const payloadKey = JSON.stringify([kind, cycleId, answers]);
    const session = epoch;
    submission = { status: "sending", kind };
    update();
    try {
      await withTimeout((signal) => client.submit(payload, { signal }), timeoutMs);
    } catch (raw) {
      const error = normalizeError(raw);
      if (disposed || session !== epoch) return "failed";
      if (kind !== "adhoc" && cycleId !== null) {
        const settledAs = settlement(error);
        if (settledAs) {
          settle(cycleId, settledAs, kind === "skip" ? "skip" : "periodic");
          return settledAs;
        }
        if (error.status === 403 && error.code === "not_due" || error.status === 404) {
          doneCycles.add(cycleId);
          submission = IDLE;
          report(error, "send");
          update();
          void load(true);
          return "withdrawn";
        }
      }
      const fieldErrors = error.status === 400 ? answerErrors(error) : {};
      const fixable = Object.keys(fieldErrors).length > 0 && payloadKey !== lastRefused;
      if (error.status === 400) lastRefused = payloadKey;
      if (kind !== "adhoc" && !fixable) {
        if (failuresCycle !== cycleId) {
          failuresCycle = cycleId;
          failures = 0;
        }
        failures += 1;
      }
      submission = {
        status: "error",
        kind,
        error,
        // Only huginn's own 400 is worth showing; a proxy's text is not.
        message: error.code === "invalid" ? (_f = fieldErrors[""]) != null ? _f : error.detail : null,
        fieldErrors
      };
      report(error, "send");
      update();
      if (kind !== "adhoc" && (error.status === 400 || error.code === "required")) void load(true);
      return error.status === 400 ? "invalid" : "failed";
    }
    if (disposed || session !== epoch) return "sent";
    if (kind === "adhoc") {
      submission = { status: "thanked", kind: "adhoc", outcome: "sent" };
      update();
    } else if (cycleId !== null) {
      settle(cycleId, "sent", kind === "skip" ? "skip" : "periodic");
    }
    return "sent";
  }
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) start();
      return () => {
        if (!listeners.delete(listener) || listeners.size > 0) return;
        void Promise.resolve().then(() => {
          if (listeners.size === 0) stop();
        });
      };
    },
    refresh: loadNow,
    setLocationKey(key) {
      if (key === locationKey) return;
      locationKey = key;
      void loadNow();
    },
    setEnabled(next) {
      if (next === enabled) return;
      enabled = next;
      epoch += 1;
      cancelRead();
      prompt = null;
      status = "idle";
      readError = null;
      paused = false;
      readFailures = 0;
      retryAt = 0;
      submission = IDLE;
      failures = 0;
      failuresCycle = null;
      lastRefused = null;
      doneCycles.clear();
      update();
      if (enabled) void loadNow();
    },
    submit: (values) => send("periodic", values),
    skip: () => send("skip", {}),
    submitAdhoc: (values) => send("adhoc", values),
    resetSubmission() {
      if (submission.status === "sending" || submission.status === "idle") return;
      submission = IDLE;
      update();
    },
    continueAnyway() {
      var _a2;
      if (!((_a2 = snapshot.periodic) == null ? void 0 : _a2.canContinue)) return;
      doneCycles.add(snapshot.periodic.cycleId);
      if (submission.status === "error") submission = IDLE;
      update();
    },
    dispose() {
      stop();
      listeners.clear();
      disposed = true;
    }
  };
}

// src/core/triggerStore.ts
function createTriggerStore() {
  let snapshot = { available: false, isOpen: false };
  const listeners = /* @__PURE__ */ new Set();
  const set = (next) => {
    if (next.available === snapshot.available && next.isOpen === snapshot.isOpen) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    open() {
      if (snapshot.available) set({ available: true, isOpen: true });
    },
    close() {
      set({ ...snapshot, isOpen: false });
    },
    setAvailable(available) {
      set({ available, isOpen: available && snapshot.isOpen });
    }
  };
}
var DEFAULT_KEY = /* @__PURE__ */ Symbol.for("huginn.defaultTriggerStore");
function defaultTriggerStore() {
  const host = globalThis;
  if (!host[DEFAULT_KEY]) host[DEFAULT_KEY] = createTriggerStore();
  return host[DEFAULT_KEY];
}

export {
  buildFeedbackContext,
  textLength,
  ratingScale,
  textLimit,
  cleanAnswers,
  formSignature,
  broadcastChannelFactory,
  createPromptController,
  createTriggerStore,
  defaultTriggerStore
};
